import type { ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/**
 * A control for choosing one (radio) or several (checkbox-group) options —
 * the promoted, token-driven replacement for the bespoke `Chip` component
 * that used to live locally in PreConsultStage with hardcoded hex colors. Any
 * option can opt into a semantic tone (RiskBadge/Badge's tokens) for its
 * active state instead of the default teal-accent selection style — this is
 * what lets a Risk-level or triage-verdict picker read its color the same way
 * a RiskBadge displaying that same value would.
 *
 * Two shapes, and the difference is meaning, not decoration:
 *
 *   single-select  → one JOINED track with hairline dividers, as drawn in the
 *                    Pre-Consult wireframe. A connected track is a universal
 *                    "exactly one of these" signal, and it reads as a scale
 *                    when the options are ordered (Low→Critical, 0→10).
 *   multi-select   → SEPARATE chips. Deliberately not joined: a joined track
 *                    promises single choice, so using it for a checkbox-group
 *                    would actively mislead.
 */
const segmentVariants = cva(
  'inline-flex items-center justify-center select-none cursor-pointer ' +
    'font-semibold transition-colors ' +
    'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-teal/25 focus-visible:relative focus-visible:z-10',
  {
    variants: {
      active: { true: '', false: '' },
      // Joined segments carry no border of their own — the track around them
      // supplies it, and the dividers come from the track's `[&>*+*]:border-l`.
      // `flex-1 min-w-0` divides the track evenly; labels wrap inside their
      // segment rather than being truncated or forcing the track wider than
      // its card.
      //
      // `rounded-none` is NOT redundant. index.css styles the bare `button`
      // element with `border-radius: var(--radius)`, and that applies to every
      // button in the product — including these. Without an explicit override
      // each segment renders as its own rounded rectangle, which bends the
      // divider between segments into a curve instead of a straight rule. The
      // legacy stylesheet beating a Tailwind utility is a known hazard here
      // (Tailwind's preflight cannot be switched on while forms still take
      // their chrome from bare element selectors), so overriding it explicitly
      // is the fix, not a tidy-up.
      joined: { true: 'flex-1 min-w-0 rounded-none text-center', false: 'rounded-full border-2 whitespace-nowrap' },
      tone: { neutral: '', success: '', warn: '', caution: '', danger: '', info: '' },
      size: {
        sm: 'px-2 py-0.5 text-[11px]',
        md: 'px-2.5 py-1 text-[12px]',
      },
      // Joined segments are the whole hit target, so they get a minimum height
      // rather than relying on label padding alone. Kept tighter than the
      // reference's 41px: this screen stacks a dozen of these down a column,
      // and at 41px each the controls dominated the form.
      joinedSize: { sm: 'min-h-[26px]', md: 'min-h-[30px]', none: '' },
    },
    compoundVariants: [
      // ── inactive
      { active: false, joined: false, class: 'border-transparent bg-muted text-ink-muted hover:bg-line/70' },
      // Transparent over the white track; hover tints toward the selected fill.
      { active: false, joined: true, class: 'bg-transparent text-ink-muted hover:bg-muted' },
      // ── active, detached (bordered chip)
      { active: true, joined: false, tone: 'neutral', class: 'border-teal bg-accent text-teal' },
      { active: true, joined: false, tone: 'success', class: 'border-success bg-success-soft text-success-ink' },
      { active: true, joined: false, tone: 'warn', class: 'border-warn bg-warn-soft text-warn-ink' },
      { active: true, joined: false, tone: 'caution', class: 'border-caution-ink bg-warn-soft text-caution-ink' },
      { active: true, joined: false, tone: 'danger', class: 'border-danger bg-danger-soft text-danger-ink' },
      { active: true, joined: false, tone: 'info', class: 'border-info bg-info-soft text-info-ink' },
      // ── active, joined (filled segment; the track draws the outline)
      { active: true, joined: true, tone: 'neutral', class: 'bg-accent text-teal' },
      { active: true, joined: true, tone: 'success', class: 'bg-success-soft text-success-ink' },
      { active: true, joined: true, tone: 'warn', class: 'bg-warn-soft text-warn-ink' },
      { active: true, joined: true, tone: 'caution', class: 'bg-warn-soft text-caution-ink' },
      { active: true, joined: true, tone: 'danger', class: 'bg-danger-soft text-danger-ink' },
      { active: true, joined: true, tone: 'info', class: 'bg-info-soft text-info-ink' },
    ],
    defaultVariants: { active: false, joined: false, tone: 'neutral', size: 'md', joinedSize: 'none' },
  }
);

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Colors this option's active state with a semantic tone instead of the default teal-accent selection — for a risk/severity/triage scale where each option carries its own meaning. */
  tone?: 'success' | 'warn' | 'caution' | 'danger' | 'info';
}

interface SingleSelectProps<T extends string> {
  multiple?: false;
  value: T | null | undefined;
  onChange: (value: T) => void;
}

interface MultiSelectProps<T extends string> {
  multiple: true;
  value: T[] | null | undefined;
  onChange: (value: T[]) => void;
}

export type SegmentedControlProps<T extends string> = {
  options: ReadonlyArray<SegmentedControlOption<T>>;
  size?: 'sm' | 'md';
  className?: string;
  /** Renders every segment inert (no click, no hover) without touching its
   * colour — the plain `button:disabled` rule (index.css) fades to 50%
   * opacity, which would wash out the very selection a read-only view exists
   * to show clearly. `disabled:opacity-100` (higher specificity than that
   * element-level rule) keeps the active segment's tone fully legible; only
   * the cursor and hover feedback change. */
  disabled?: boolean;
} & (SingleSelectProps<T> | MultiSelectProps<T>);

export function SegmentedControl<T extends string>(props: SegmentedControlProps<T>) {
  const { options, size = 'md', className, disabled } = props;
  const joined = !props.multiple;
  const selected = new Set<T>(props.multiple ? props.value ?? [] : props.value != null ? [props.value] : []);

  const toggle = (opt: T) => {
    if (props.multiple) {
      const cur = props.value ?? [];
      props.onChange(cur.includes(opt) ? cur.filter((v) => v !== opt) : [...cur, opt]);
    } else {
      props.onChange(opt);
    }
  };

  // The joined track owns the outline, the radius and the dividers: a white
  // track with an opaque outline and equal-width segments, the shape the
  // reference implementation uses and the one the Pre-Consult wireframe draws.
  // Both end caps are rounded; the vertical rules are the dividers between
  // segments, nothing else.
  //
  // `border-control-line` rather than `border-line` is load-bearing: see that
  // token's own note in theme.css. A translucent hairline loses alpha twice on
  // a scaled display and drops out asymmetrically, which is why this control
  // kept looking like it had a left border and no right one.
  //
  // `w-full` with `flex-1` children, so segments divide the track evenly
  // instead of each sizing to its own label. That is what makes it read as one
  // scale rather than a row of buttons, and it gives an 11-point pain scale
  // even, predictable touch targets.
  //
  // No `flex-wrap`: equal flexible segments shrink to fit instead, so the
  // track can neither overflow its card nor wrap to a second row — which also
  // removes the case where a pill radius took half a doubled height and turned
  // the whole control into a circle.
  const trackClass = joined
    ? 'flex w-full max-w-[420px] items-stretch overflow-hidden rounded-[16px] border border-control-line bg-card [&>*+*]:border-l [&>*+*]:border-control-line'
    : 'flex flex-wrap gap-2';

  return (
    <div className={cn(trackClass, className)} role={props.multiple ? 'group' : 'radiogroup'}>
      {options.map((opt) => {
        const active = selected.has(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            role={props.multiple ? 'checkbox' : 'radio'}
            aria-checked={active}
            disabled={disabled}
            onClick={() => toggle(opt.value)}
            className={cn(
              segmentVariants({ active, joined, tone: active ? opt.tone ?? 'neutral' : 'neutral', size, joinedSize: joined ? size : 'none' }),
              disabled && 'pointer-events-none disabled:opacity-100 cursor-default'
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
